// Public venue-list card — collapses/expands between a compact photo tile
// and the full ExpandedVenueCard body. Extracted from GuestMapSurface.js.

import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock3, Navigation, Star } from 'lucide-react';
import { VenueReviewsModal } from '../VenueReviewsModal';
import { VenueFavoriteButton } from './VenueFavoriteButton';
import { VenueEventChip } from './VenueEventChip';
import { ExpandedVenueCard } from './ExpandedVenueCard';
import { distanceKmFromUser } from './distance';
import { getVenueImage } from './venueImage';
import {
  formatVenueCardDescription,
  formatVenueCategoryLabel,
  formatVenueDistance,
  formatWalkingTime,
  venueOpenStatusLabel,
} from './venueFormatters';

const font = "'Inria Serif', serif";

export function VenueCard({
  venue, idx, onClick, isExpanded, onClose, onMessage,
  userPosition, venueEvents = [], hasActivePass = true,
}) {
  const image = getVenueImage(venue, idx);
  const distanceText = formatVenueDistance(userPosition, venue);
  const walkingTime = formatWalkingTime(userPosition, venue);
  const closesAt = venueOpenStatusLabel(venue);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const soonestEvent = venueEvents?.[0];
  // Distance-based "far away" hint — surfaces which saved/pinned venues
  // aren't actually nearby. NEARBY_RADIUS_KM is 20km app-wide.
  const distanceKm = distanceKmFromUser(venue, userPosition);
  const isFarAway = Number.isFinite(distanceKm) && distanceKm > 20;

  return (
    <motion.div
      layout
      transition={{ type: 'spring', stiffness: 320, damping: 34, mass: 0.8 }}
      data-testid={`venue-card-wrapper-${venue.id}`}
      style={{ marginBottom: 14 }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isExpanded ? (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <ExpandedVenueCard
              venue={venue}
              idx={idx}
              onClose={onClose}
              onMessage={onMessage}
              onOpenReviews={() => setReviewsOpen(true)}
              venueEvents={venueEvents}
              hasActivePass={hasActivePass}
            />
          </motion.div>
        ) : (
          <motion.div
            key="compact"
            onClick={onClick}
            data-testid={`venue-card-${venue.id}`}
            className="premium-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            whileTap={{ scale: 0.985 }}
            style={{ borderRadius: 26, overflow: 'hidden', cursor: 'pointer' }}
          >
            <div style={{ position: 'relative' }}>
              <img src={image} alt={venue.name} style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }} />
              <div
                style={{ position: 'absolute', top: 12, left: 12 }}
                onClick={(event) => event.stopPropagation()}
              >
                <VenueFavoriteButton venueId={venue.id} testIdPrefix="venue-card-favorite" />
              </div>
              <button
                type="button"
                data-testid={`venue-card-rating-${venue.id}`}
                onClick={(event) => { event.stopPropagation(); setReviewsOpen(true); }}
                aria-label="See ratings & reviews"
                style={{ position: 'absolute', top: 12, right: 12, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderRadius: 999, background: 'rgba(18,18,28,0.55)', color: '#FFFFFF', fontFamily: font, fontSize: 13, fontWeight: 700, backdropFilter: 'blur(6px)', border: 'none', cursor: 'pointer' }}
              >
                {Number(venue.rating) > 0 ? (
                  <>
                    <Star size={13} fill="#F4C54E" color="#F4C54E" />
                    {venue.rating}
                  </>
                ) : 'New'}
              </button>
            </div>

            <div style={{ padding: '14px 16px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={{ fontFamily: font, fontSize: 21, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.08 }}>{venue.name}</p>
                  <p style={{ fontFamily: font, fontSize: 13.5, fontWeight: 700, color: 'var(--text-2)', margin: '6px 0 0', letterSpacing: '0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatVenueCategoryLabel(venue)}</p>
                  {formatVenueCardDescription(venue) ? (
                    <p data-testid={`venue-card-description-${venue.id}`} style={{ fontFamily: font, fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)', margin: '3px 0 0', lineHeight: 1.4, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{formatVenueCardDescription(venue)}</p>
                  ) : null}
                </div>
                <div data-testid={`venue-card-open-state-${venue.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderRadius: 999, background: venue.is_open_now === false ? 'rgba(214, 50, 50, 0.10)' : 'rgba(17, 163, 82, 0.08)', color: venue.is_open_now === false ? '#B33232' : '#138F4E', fontFamily: font, fontSize: 12.5, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <Clock3 size={12} />
                  {closesAt}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontFamily: font, fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600, flexWrap: 'wrap' }}>
                {isFarAway ? (
                  <span
                    data-testid={`venue-card-far-${venue.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, background: 'rgba(255, 153, 51, 0.14)', color: '#C46A12', fontFamily: font, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.01em' }}
                  >
                    Far from you
                  </span>
                ) : null}
                <Navigation size={12} strokeWidth={2.2} />
                <span>{distanceText}</span>
                {walkingTime && !isFarAway ? (
                  <>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span>{walkingTime}</span>
                  </>
                ) : null}
              </div>

              {soonestEvent ? (
                <div style={{ marginTop: 10 }}>
                  <VenueEventChip event={soonestEvent} compact />
                </div>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <VenueReviewsModal open={reviewsOpen} onClose={() => setReviewsOpen(false)} venue={venue} />
    </motion.div>
  );
}
